import {
  FilterBarContainer,
  FilterBarLeftContent,
  FiltersContainerLeftContainer,
  FiltersContainerRightContainer,
} from "./FilterBarLog.styled";
import {
  Select,
  Button,
  Autocomplete,
  TabSwitcher,
} from "@ann/web-components";
import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTriggersLogs } from "../../context/TriggersLogsContext";

const FilterBarLog = ({ mode, setMode }) => {
  const { filters, updateFilters } = useTriggersLogs();
  const [searchTermAction, setSearchTermAction] = useState("");
  const [searchTermLastUpdate, setSearchTermLastUpdate] = useState("");
  const [searchTermStatus, setSearchTermStatus] = useState("");
  const [searchTermModifiedBy, setSearchTermModifiedBy] = useState("");
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialTab = () => {
    if (location.pathname.includes('/logs')) {
      return "logs";
    }
    return "config";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  useEffect(() => {
    const newTab = getInitialTab();
    setSelectedTab(newTab);
  }, [location.pathname]);

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    if (tabId === "created") {
      navigate(`/v2/workflows`);
   } else if (tabId === "templates") {
      navigate(`/v2/workflows/templates`);
    } else if (tabId === "logs") {
      navigate(`/v2/workflows/logs`);
    }
  };

  const autocompleteRef = useRef(null);

  const actionOptions = [
    { label: "Created", value: "Created" },
    { label: "Modified", value: "Modified" },
    { label: "Activated", value: "Activated" },
    { label: "Deactivated", value: "Deactivated" },
  ];

  const statusOptions = [
    { label: "On", value: "on" },
    { label: "Off", value: "off" },
  ];

  const lastUpdateOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This year", value: "this_year" },
  ];

  const modifiedByOptions = [
    { label: "John Doe", value: "john_doe" },
    { label: "Jane Smith", value: "jane_smith" },
    { label: "Mike Johnson", value: "mike_johnson" },
    { label: "Sarah Wilson", value: "sarah_wilson" },
    { label: "Alex Brown", value: "alex_brown" },
    { label: "Lisa Davis", value: "lisa_davis" },
    { label: "David Chen", value: "david_chen" },
    { label: "Emma Wilson", value: "emma_wilson" },
    { label: "Tom Anderson", value: "tom_anderson" },
    { label: "Rachel Green", value: "rachel_green" },
    { label: "Chris Lee", value: "chris_lee" },
    { label: "Maria Garcia", value: "maria_garcia" },
    { label: "Kevin Park", value: "kevin_park" },
    { label: "Sophie Turner", value: "sophie_turner" },
    { label: "James Bond", value: "james_bond" },
  ];

  const filteredAction = useMemo(() => {
    if (!searchTermAction.trim()) return actionOptions;
    const searchLower = searchTermAction.toLowerCase();
    return actionOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermAction]);

  const filteredLastUpdate = useMemo(() => {
    if (!searchTermLastUpdate.trim()) return lastUpdateOptions;
    const searchLower = searchTermLastUpdate.toLowerCase();
    return lastUpdateOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermLastUpdate]);

  const filteredStatus = useMemo(() => {
    if (!searchTermStatus.trim()) return statusOptions;
    const searchLower = searchTermStatus.toLowerCase();
    return statusOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermStatus]);

  const filteredModifiedBy = useMemo(() => {
    if (!searchTermModifiedBy.trim()) return modifiedByOptions;
    const searchLower = searchTermModifiedBy.toLowerCase();
    return modifiedByOptions.filter(
      (modifiedBy) =>
        modifiedBy.label.toLowerCase().includes(searchLower) ||
        modifiedBy.value.toLowerCase().includes(searchLower)
    );
  }, [searchTermModifiedBy]);

  const handleMultipleSelectionAction = (items) => {
    updateFilters({ action: items });
  };

  const handleMultipleSelectionLastUpdate = (items) => {
    updateFilters({ lastUpdate: items });
  };

  const handleMultipleSelectionStatus = (items) => {    
    updateFilters({ status: items });
  };

  const handleSearchModifiedBy = (searchValue) => {    
    setSearchTermModifiedBy(searchValue);
  };

  const handleMultipleSelectionModifiedBy = (items) => {    
    updateFilters({ modifiedBy: items.map(item => item.value) });
  };

  const handleAutocompleteChange = (value) => {
    setAutocompleteValue(value);
    updateFilters({ search: value });
  };

  const handleAutocompleteClear = () => {
    setAutocompleteValue("");
    updateFilters({ search: "" });
    setAutocompleteKey((prev) => prev + 1);

    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector("input");
      if (input) {
        input.value = "";
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  };

  const handleClearAllFilters = () => {
    updateFilters({
      search: '',
      action: [],
      lastUpdate: '',
      status: [],
      modifiedBy: []
    });
    setSearchTermAction("");
    setSearchTermLastUpdate("");
    setSearchTermStatus("");
    setSearchTermModifiedBy("");
    setAutocompleteValue("");
    setAutocompleteKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
  };

  const emptyOrValue = (arr) => (arr.length > 0 ? arr : undefined);

  return (
    <FilterBarContainer>
      <FilterBarLeftContent>
        <FiltersContainerLeftContainer>
          <TabSwitcher
            key={selectedTab}
            defaultSelectedId={selectedTab}
            size="medium"
            onChange={handleTabChange}
          >
           <TabSwitcher.Button id="created" label="Created" />
            <TabSwitcher.Button id="templates" label="Templates" />
            <TabSwitcher.Button id="logs" label="Audit Log" />
          </TabSwitcher>
        </FiltersContainerLeftContainer>
        <FiltersContainerRightContainer>
          <Autocomplete
            style={{ fontSize: "5px" }}
            key={autocompleteKey}
            ref={autocompleteRef}
            value={autocompleteValue}
            onChange={handleAutocompleteChange}
            onSearch={() => {}}
            onSuggestionSelect={() => {}}
            onClear={handleAutocompleteClear}
            placeholder="Search Logs..."
            suggestions={[
              "Welcome Email Event",
              "Appointment Reminder",
              "Product Launch Notification",
              "Payment Webhook",
              "Customer Onboarding",
              "Marketing Campaign",
              "Support Ticket Created",
              "Payment Failed Alert",
            ]}
          />
          <Select
            key={`action-filter-${filtersKey}`}
            label="Actions"  
            placeholder="Actions"
            onChange={handleMultipleSelectionAction}
            size="small"
            value={emptyOrValue(filters.action)}
            width="auto"
            minWidth="139px"
          >
            <Select.Menu>
              {filteredAction.map((action) => (
                <Select.Item
                  key={action.value}
                  value={action.value}
                  label={action.label}
                >
                  {action.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`last-update-filter-${filtersKey}`}
            label="Last Update"
            placeholder="Last Update"
            onChange={handleMultipleSelectionLastUpdate}
            size="small"
            value={emptyOrValue(filters.lastUpdate)}
            width="auto"
            minWidth="139px" 
            disabled={true}
          >
            <Select.Menu>
              {filteredLastUpdate.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Select
            key={`modified-by-filter-${filtersKey}`}
            label="Modified By"
            placeholder="Modified By"
            onSelectionChange={handleMultipleSelectionModifiedBy}
            size="small"
            value={emptyOrValue(filters.modifiedBy)}
            width="auto"
            minWidth="144px"
          >
            <Select.Menu multipleChoice={true}>
              <Select.Search
                placeholder="Search a name"
                onChange={handleSearchModifiedBy}
                label=""
              />
              {filteredModifiedBy.map((modifiedBy) => (
                <Select.Item
                  key={modifiedBy.value}
                  value={modifiedBy.value}
                  label={modifiedBy.label}
                >
                  {modifiedBy.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFilters}
          />
        </FiltersContainerRightContainer>
      </FilterBarLeftContent>
    </FilterBarContainer>
  );
};

export default FilterBarLog;
